import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { Building2 } from 'lucide-react';

interface SidebarProps {
  isCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

interface MenuItem {
  label: string;
  path: string;
  icon: string;
  tooltip?: string;
}

const Sidebar = ({ isCollapsed = false, onCollapsedChange }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(isCollapsed);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const menuItems = [
    {
      icon: Building2,
      label: 'Gerenciar Empresa',
      path: '/company-management',
      isActive: location.pathname === '/company-management',
    },
    {
      label: 'Dashboard',
      path: '/tax-consultation-dashboard',
      icon: 'LayoutDashboard',
      tooltip: 'Visão geral e métricas operacionais'
    },
    {
      label: 'Nova Consulta',
      path: '/new-tax-consultation-form',
      icon: 'FilePlus',
      tooltip: 'Criar nova consulta tributária'
    },
    {
      label: 'Histórico',
      path: '/consultation-history',
      icon: 'History',
      tooltip: 'Consultas anteriores e análises'
    },
    {
      label: 'Configurações',
      path: '/user-profile-and-system-settings',
      icon: 'Settings',
      tooltip: 'Preferências e configurações do sistema'
    }
  ];

  useEffect(() => {
    setCollapsed(isCollapsed);
  }, [isCollapsed]);

  const handleCollapse = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    onCollapsedChange?.(newState);
  };

  const handleMobileToggle = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const handleLogout = () => {
    // Clear authentication data
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    sessionStorage.clear();
    
    // Navigate to login
    navigate('/login-screen');
    
    // Close confirmation
    setShowLogoutConfirm(false);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.altKey) {
        const keyMap: { [key: string]: string } = {
          '1': '/tax-consultation-dashboard',
          '2': '/new-tax-consultation-form',
          '3': '/consultation-history',
          '4': '/user-profile-and-system-settings'
        };
        
        if (keyMap[e.key]) {
          e.preventDefault();
          window.location.href = keyMap[e.key];
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <>
      <button
        onClick={handleMobileToggle}
        className="lg:hidden fixed top-4 left-4 z-[250] p-2 bg-card rounded-md shadow-card"
        aria-label="Toggle menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} color="var(--color-foreground)" />
      </button>

      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-[90]"
          onClick={handleMobileToggle}
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 h-screen bg-card border-r border-border z-[100]
          transition-all duration-300 ease-out
          ${collapsed ? 'w-20' : 'w-64'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        role="navigation"
        aria-label="Navegação principal"
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-4 border-b border-border">
            {!collapsed && (
              <Link to="/tax-consultation-dashboard" className="flex items-center gap-2">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="flex-shrink-0"
                >
                  <rect width="32" height="32" rx="6" fill="var(--color-primary)" />
                  <path
                    d="M8 12h6v-2H8v2zm0 4h6v-2H8v2zm0 4h6v-2H8v2zm8-8h8v-2h-8v2zm0 4h8v-2h-8v2zm0 4h8v-2h-8v2z"
                    fill="var(--color-primary-foreground)"
                  />
                  <path
                    d="M10 22l6-6-6-6v12z"
                    fill="var(--color-accent)"
                  />
                </svg>
                <span className="text-lg font-semibold text-foreground">TaxQuery Pro</span>
              </Link>
            )}
            
            <button
              onClick={handleCollapse}
              className="hidden lg:flex p-2 hover:bg-muted rounded-md transition-colors duration-150"
              aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            >
              <Icon
                name={collapsed ? 'ChevronRight' : 'ChevronLeft'}
                size={20}
                color="var(--color-muted-foreground)"
              />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-1 px-2">
              {menuItems.map((item, index) => {
                const active = isActive(item.path);
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`
                        flex items-center gap-3 px-3 py-2.5 rounded-md
                        transition-all duration-150 ease-out
                        ${active
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }
                        ${collapsed ? 'justify-center' : ''}
                      `}
                      title={collapsed ? item.tooltip : undefined}
                      aria-label={`${item.label} (Alt+${index + 1})`}
                      onClick={() => setIsMobileOpen(false)}
                    >
                      <Icon
                        name={item.icon}
                        size={20}
                        color="currentColor"
                      />
                      {!collapsed && (
                        <span className="font-medium">{item.label}</span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="border-t border-border p-4">
            <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
              <div className="relative">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <Icon name="User" size={16} color="var(--color-primary-foreground)" />
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success rounded-full border-2 border-card" />
              </div>
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">Usuário</p>
                  <p className="text-xs text-muted-foreground truncate">Sistema Online</p>
                </div>
              )}
              {!collapsed && (
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  className="p-2 hover:bg-destructive/10 rounded-md transition-colors"
                  aria-label="Sair"
                  title="Sair do Sistema"
                >
                  <Icon name="LogOut" size={16} color="var(--color-destructive)" />
                </button>
              )}
            </div>
          </div>
        </div>
      </aside>

      {showLogoutConfirm && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowLogoutConfirm(false)}
          />
          <div className="relative bg-card rounded-lg shadow-modal w-full max-w-sm p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-2 bg-destructive/10 rounded-full">
                <Icon name="AlertTriangle" size={24} color="var(--color-destructive)" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-1">Confirmar Logout</h3>
                <p className="text-sm text-muted-foreground">
                  Tem certeza que deseja sair do sistema? Você precisará fazer login novamente.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 rounded-md transition-colors"
              >
                Sair
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;